import "../styles/Home.css";
import { ReactComponent as NftText } from "../assets/img/home/NFT-Fundraising-text.svg";
import CellsImgMobile from "../assets/img/home/cells-img-mobile.png";
import CellsImgDesktop from "../assets/img/home/cells-img-desktop.png";
import CellsImgDesktopFull from "../assets/img/home/cells-img-desktop-full.png";

import HomeMainImg from "../assets/img/home/home-main-img.svg";
import HomeMainImgMobile from "../assets/img/home/home-main-img-mobile.png";
import AnimatedNumber from "../components/common/AnimatedNumber";
import OpenFundingCalls from "../components/home/OpenFundingCalls";

import HighestProject from "../components/home/HighestProject";
import HomeFAQ from "../components/home/HomeFAQ";
import { Link } from "react-router-dom";
import Container from "../components/common/Container";

import useAuth from "../hooks/useAuth";
import { Bars } from "react-loader-spinner";
import useHandleCreateProject from "../hooks/useHandleCreateProject";
import ProcessSection from "../components/home/ProcessSection";
import { useQuickReportQuery } from "../redux/slices/api/projectsApiSlice";
import ResultDiscoverySection from "../components/home/ResultDiscoverySection";
import HomeCommunities from "../components/home/HomeCommunities";
import RecentJoinedResearchers from "../components/home/RecentJoinedResearchers";
import TeamSection from "../components/home/TeamSection";
import RoadmapSection from "../components/home/RoadmapSection";
import FeaturesSection from "../components/home/FeaturesSection";
import SupportedBlockchains from "../components/home/SupportedBlockchains";
import { HiArrowRight, HiPlay } from "react-icons/hi";

const Home = () => {
  const { authLoading } = useAuth();
  const { handleCreateProject } = useHandleCreateProject();
  const handleDefineProject = async () => {
    handleCreateProject();
  };
  const { data: quickReportData } = useQuickReportQuery();

  return (
    <div className="w-full h-full flex flex-col items-center">
      {/* Hero Section - Ultra Modern Design */}
      <div className="w-full min-h-screen hero-section-bg flex flex-col items-center relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/30 rounded-full blur-[150px] animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-primary-green/20 rounded-full blur-[180px] animate-pulse-slow animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#7e7cff]/10 rounded-full blur-[200px]"></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none"></div>
        
        <Container>
          <section className="w-full min-h-[calc(100vh-80px)] flex flex-col justify-center items-center pt-32 pb-12">
            <div className="w-full flex justify-between mlg:flex-row flex-col-reverse gap-12 lg:gap-16 items-center">
              {/* Left content column */}
              <div className="flex 2xl:w-[55%] mlg:w-[60%] w-full flex-col items-start z-10">
                {/* Main headline with dramatic typography */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.1] abel-font uppercase text-white tracking-tight animate-fade-in-up animation-delay-100">
                  Fund the Future of{" "}
                  <span className="hero-gradient-text">
                    Decentralized Science
                  </span>
                </h1>
                
                {/* DeSci Asia Badge */}
                <div className="flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 w-fit animate-fade-in-up animation-delay-150">
                  <span className="text-white/70 text-sm font-medium">Powered by</span>
                  <span className="text-white font-bold text-sm">DeSci Asia</span>
                </div>
                
                {/* Subheadline with better typography */}
                <p className="text-white/60 mt-6 lg:mt-8 lg:leading-relaxed roboto-font text-base lg:text-lg xl:text-xl max-w-2xl animate-fade-in-up animation-delay-200">
                  Tokenize your research, launch loyalty NFT communities for dedicated supporters, and transform how science is funded and collaborated on.
                </p>
                
                {/* CTA Buttons - More prominent design */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center mt-10 gap-4 animate-fade-in-up animation-delay-300">
                  <Link
                    to="/communities"
                    className="group relative px-8 py-4 text-white flex justify-center items-center gap-2 bg-gradient-to-r from-primary via-[#9c5cff] to-primary rounded-2xl text-base font-semibold overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-primary/40"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-primary-green to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                    <span className="relative flex items-center gap-2">
                      Explore Communities
                      <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                  <Link
                    to={"projects"}
                    className="group px-8 py-4 text-white/90 flex justify-center items-center gap-2 rounded-2xl border border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300 backdrop-blur-sm"
                  >
                    <HiPlay className="text-primary-green" />
                    Explore Projects
                  </Link>
                </div>

                {/* Stats Section - Compact inline design */}
                <div className="flex flex-wrap gap-3 lg:gap-4 mt-12 xl:mt-16 animate-fade-in-up animation-delay-400">
                  <div className="flex items-center gap-3 px-4 py-3 lg:px-5 lg:py-3.5 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-lg bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-base lg:text-lg font-bold">
                        <AnimatedNumber number={quickReportData?.artifacts || 0} />
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg lg:text-xl font-bold text-white abel-font leading-tight">Artifacts</span>
                      <span className="text-white/50 text-xs leading-tight">Published on-chain</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 px-4 py-3 lg:px-5 lg:py-3.5 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-lg bg-gradient-to-br from-primary-green to-[#0dcada] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-base lg:text-lg font-bold">
                        <AnimatedNumber number={quickReportData?.contributions || 0} />
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg lg:text-xl font-bold text-white abel-font leading-tight">Contributions</span>
                      <span className="text-white/50 text-xs leading-tight">Funds & memberships</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 px-4 py-3 lg:px-5 lg:py-3.5 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-lg bg-gradient-to-br from-[#7e7cff] to-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-base lg:text-lg font-bold">
                        <AnimatedNumber number={quickReportData?.users || 0} />
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg lg:text-xl font-bold text-white abel-font leading-tight">Researchers</span>
                      <span className="text-white/50 text-xs leading-tight">Active community</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right image column - Enhanced 3D feel */}
              <div className="mlg:flex-1 flex justify-center mlg:justify-end items-center relative">
                <div className="relative hero-image-container">
                  {/* Multiple layered glow effects */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/50 via-[#7e7cff]/30 to-primary-green/50 blur-[80px] scale-110 rounded-full animate-pulse-slow"></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary-green/40 to-primary/40 blur-[50px] scale-95 rounded-full animate-pulse-slow animation-delay-1000"></div>
                  
                  {/* Rotating ring effect */}
                  <div className="absolute inset-[-20%] border-2 border-dashed border-white/10 rounded-full animate-spin-slow"></div>
                  
                  <img
                    src={HomeMainImg}
                    alt="Decentralized Science Illustration"
                    className="hidden mlg:block relative z-10 h-auto max-h-[280px] lg:max-h-[350px] xl:max-h-[400px] 2xl:max-h-[450px] drop-shadow-2xl hero-float-animation"
                  />
                </div>
                <div className="w-full flex justify-center mlg:hidden">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/40 to-primary-green/40 blur-[60px] scale-90 rounded-full"></div>
                    <img
                      src={HomeMainImgMobile}
                      alt="Decentralized Science Illustration"
                      className="relative z-10 w-fit max-h-[300px] drop-shadow-xl hero-float-animation"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce hidden lg:flex">
              <span className="text-white/40 text-xs uppercase tracking-widest">Scroll</span>
              <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
                <div className="w-1.5 h-3 bg-white/40 rounded-full animate-scroll-indicator"></div>
              </div>
            </div>
          </section>
        </Container>
      </div>

      {/* How It Works Section - Completely redesigned */}
      <ProcessSection handleCreateProject={handleCreateProject} authLoading={authLoading} />
      {/* Communities Section */}
      <section className="w-full py-20 lg:py-28 bg-gradient-to-b from-secondary via-[#1a1230] to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#7e7cff]/30 to-transparent"></div>
        <div className="absolute top-1/4 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 -left-40 w-80 h-80 bg-primary-green/5 rounded-full blur-[100px] pointer-events-none"></div>
        <Container className="relative z-10">
          <HomeCommunities />
        </Container>
      </section>

      {/* Open Funding Calls Section */}
      <section className="w-full flex flex-col items-center bg-gradient-to-b from-white via-whitesmoke to-white relative overflow-hidden py-20 lg:py-28">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
        <div className="absolute top-1/4 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-primary-green/5 rounded-full blur-[100px] pointer-events-none"></div>
        
        <Container className="relative z-10">
          <OpenFundingCalls />
        </Container>
      </section>

      {/* Results Discovery Section */}
      <div className="w-full bg-gradient-to-b from-secondary via-[#1a1230] to-secondary flex justify-center relative overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-green/30 to-transparent"></div>
        <Container className="relative z-10">
          <ResultDiscoverySection />
        </Container>
      </div>

      {/* Recently Joined Researchers Section */}
      <section className="w-full flex flex-col items-center bg-gradient-to-b from-white via-whitesmoke to-white relative overflow-hidden py-20 lg:py-28">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-primary-green/5 rounded-full blur-[100px] pointer-events-none"></div>
        <Container className="flex flex-col relative z-10">
          <RecentJoinedResearchers />
        </Container>
      </section>

      {/* Highest Funded Projects Section */}
      <section className="w-full py-12 lg:py-16 bg-gradient-to-b from-secondary via-[#1a1230] to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="relative z-10 w-full flex justify-center">
          <div className="w-full">
            <HighestProject />
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <FeaturesSection />

      {/* Team Section */}
      <TeamSection />

      {/* Roadmap Section */}
      <RoadmapSection />

      {/* Supported Blockchains */}
      <SupportedBlockchains />
      
      {/* FAQ Section */}
      <section className="w-full flex justify-center bg-gradient-to-b from-white via-whitesmoke to-white overflow-hidden relative py-20 lg:py-28">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-green/20 to-transparent"></div>
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 -right-40 w-80 h-80 bg-primary-green/5 rounded-full blur-[100px] pointer-events-none"></div>
        <Container className="relative z-10 w-full">
          <HomeFAQ />
        </Container>
      </section>
    </div>
  );
};

export default Home;
